import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "layout__wrapper" }
const _hoisted_2 = { class: "layout__wrapper" }

import { computed, onBeforeMount, onBeforeUnmount, Raw, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  useRoute,
  useRouter,
} from 'vue-router'

import { backend } from '@/services/backend'
import { scrollIntoView } from '@/helpers/dom'
import { Mixpanel } from '@/analytics/mixpanel'

import { meta } from '@/services/meta'
import { Sentry } from '@/services/sentry'
import { ReadingProgress } from '@/services/readingProgress'
import { assertNotNull } from '@/helpers/typing'

import { ArticleView } from '@/models/doc.article'

import Layout from '@/app/components/Layout.vue'
import ArticleContent from '@/app/components/article/Content.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'ArticlePage',
  setup(__props, { expose: __expose }) {

const route = useRoute()
const router = useRouter()
const wait = useWait()

const article = ref<Raw<ArticleView> | null>(null)

const readingProgress = ref<ReadingProgress | null>(null)
// For tests, to permanently disable timers,
// can be set by `disableTimers()` method call.
const timersDisabled = ref(false)

const readingStartTime = ref(0)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component has changed,
    // but this component is reused in the new route.
    await updateReadTime()
    // We're navigating to a new article, but the component is reused (not
    // re-rendered) so beforeMount() isn't called. Thus we need to load the new
    // article's data here.
    await loadDocument(to)
    scrollIntoView('.article-single__content-editor')

    next()
  },
)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    await updateReadTime()
    next()
  },
)

onBeforeMount(async () => {
  await loadDocument(route)
})

onBeforeUnmount(async () => {
  if (readingProgress.value) {
    assertNotNull(readingProgress.value).destroy()
  }
})

async function updateReadTime(): Promise<void> {
  // Updates the reading time, to be used when we navigate away from this page.
  const timeDiff = Math.floor((Date.now() - readingStartTime.value) / 1000)
  await Mixpanel.trackDocReadTime(article.value as ArticleView, timeDiff)
}

async function _fetchDocument(
  route: RouteLocationNormalized,
): Promise<ArticleView> {
  const response = await backend.getArticle(route.params.url_slug as string)
  const articleView = new ArticleView(response)
  readingStartTime.value = Date.now()
  return articleView
}

function disableTimers(): void {
  timersDisabled.value = true
  if (readingProgress.value) {
    assertNotNull(readingProgress.value).clearTimers()
  }
}

function restartTracking(): void {
  if (!timersDisabled.value) {
    assertNotNull(readingProgress.value).restartTracking()
  }
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')
    article.value = await _fetchDocument(route)
    readingProgress.value = new ReadingProgress(
      assertNotNull(article.value) as ArticleView,
    )
    // Set the current page from current route.
    updateMeta()

    // Start the 20 sec timer to add the document to the user library
    // or update the access time.
    restartTracking()
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    wait.end('Loading Doc')
  }
}

function updateMeta(): void {
  meta.setAppTitle(assertNotNull(article.value).title)
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})

__expose({
  disableTimers,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (isLoadingDoc.value)
      ? (_openBlock(), _createBlock(Layout, { key: 0 }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(LoadingSpinner)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (article.value)
      ? (_openBlock(), _createBlock(ArticleContent, {
          key: 1,
          article: article.value
        }, null, 8, ["article"]))
      : _createCommentVNode("", true),
    (!article.value && isErrorLoadingDoc.value)
      ? (_openBlock(), _createBlock(Layout, { key: 2 }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(LoadingError)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})